import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../App';

const AMBER = '#FAAB1C', BLUE = '#21A84A', NAVY = '#0C2417';
const STAGE_COLOR = { signed: '#FAAB1C', site_survey: '#21A84A', design: '#7C3AED', permitting: '#EA580C', installation: '#0891B2', inspection: '#DB2777', pto: '#16A34A', complete: '#0C2417', on_hold: '#94A3B8' };
const $ = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const Stat = ({ label, value, sub, accent, testid }) => (
  <div className="card p-4" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{label}</p>
    <p className="text-2xl font-heading font-black mt-1" style={{ color: accent || undefined }}>{value}</p>
    {sub && <p className="text-[11px] text-text-muted mt-0.5">{sub}</p>}
  </div>
);

function ProjectCard({ p, stages, onMove, onOpen }) {
  const idx = stages.findIndex(s => s.key === p.stage);
  const next = stages[idx + 1];
  return (
    <div className={`rounded-xl border bg-surface p-3 space-y-2 shadow-sm hover:shadow-md transition-shadow ${p.behind_schedule ? 'border-red-300' : 'border-border'}`} data-testid={`project-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <button onClick={() => onOpen(p)} className="text-left min-w-0" data-testid={`project-open-${p.id}`}>
          <p className="text-sm font-semibold text-text-primary truncate">{p.lead_name}</p>
          <p className="text-[11px] text-text-muted truncate">{p.address} · {p.city}, {p.state}</p>
        </button>
        {p.behind_schedule && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 flex-shrink-0">Behind</span>}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-text-secondary">
        <span className="font-mono font-semibold">{p.system_size_kw} kW</span>
        <span className="font-mono">{$(p.total_cost)}</span>
        {p.include_battery && <i className="fa-solid fa-car-battery text-blue-500" title="Battery" />}
        <span className="ml-auto text-text-muted"><i className="fa-regular fa-clock mr-1" />{p.days_in_stage}d</span>
      </div>
      {p.rep_name && <p className="text-[10px] text-text-muted"><i className="fa-solid fa-user mr-1" />{p.rep_name}</p>}
      {next && p.stage !== 'on_hold' && (
        <button onClick={() => onMove(p, next.key)} className="w-full text-[11px] font-bold py-1.5 rounded-lg text-white hover:brightness-110 transition-all" style={{ background: STAGE_COLOR[next.key] }} data-testid={`advance-${p.id}`}>
          <i className="fa-solid fa-arrow-right mr-1" />{next.label}
        </button>
      )}
    </div>
  );
}

function ProjectDrawer({ p, stages, onClose, onPatch }) {
  const [note, setNote] = useState('');
  const [installer, setInstaller] = useState(p.installer || '');
  const [date, setDate] = useState(p.target_install_date || '');
  if (!p) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md z-50 bg-surface shadow-2xl overflow-y-auto animate-slide-down" data-testid="project-drawer">
        <div className="p-5 text-white" style={{ background: NAVY }}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] font-bold tracking-widest" style={{ color: AMBER }}>INSTALL PROJECT</p>
              <h3 className="text-xl font-heading font-black mt-1">{p.lead_name}</h3>
              <p className="text-xs text-slate-300">{p.address} · {p.city}, {p.state} {p.zip}</p>
            </div>
            <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="project-drawer-close"><i className="fa-solid fa-xmark text-lg" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
            <div><p className="text-[10px] text-slate-400">System</p><p className="font-bold">{p.system_size_kw} kW · {p.panel_count} panels</p></div>
            <div><p className="text-[10px] text-slate-400">Contract</p><p className="font-bold">{$(p.total_cost)}</p></div>
            <div><p className="text-[10px] text-slate-400">Day</p><p className="font-bold">{p.days_since_signed} since signing</p></div>
          </div>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Stage</p>
            <div className="flex flex-wrap gap-1.5">
              {[...stages, { key: 'on_hold', label: 'On Hold' }].map(s => (
                <button key={s.key} onClick={() => onPatch(p, { stage: s.key })} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all ${p.stage === s.key ? 'text-white' : 'text-text-secondary border-border hover:border-primary'}`} style={p.stage === s.key ? { background: STAGE_COLOR[s.key], borderColor: STAGE_COLOR[s.key] } : {}} data-testid={`stage-btn-${s.key}`}>{s.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Install crew</label><input value={installer} onChange={e => setInstaller(e.target.value)} onBlur={() => installer !== (p.installer || '') && onPatch(p, { installer })} className="input-field h-9 text-sm" placeholder="Crew / subcontractor" data-testid="project-installer-input" /></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Target install</label><input type="date" value={date} onChange={e => { setDate(e.target.value); onPatch(p, { target_install_date: e.target.value }); }} className="input-field h-9 text-sm" data-testid="project-date-input" /></div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Homeowner</p>
            <div className="text-xs text-text-secondary space-y-1">
              {p.homeowner_phone && <p><i className="fa-solid fa-phone w-4" />{p.homeowner_phone}</p>}
              {p.homeowner_email && <p><i className="fa-solid fa-envelope w-4" />{p.homeowner_email}</p>}
              <p><i className="fa-solid fa-user-tie w-4" />Rep: {p.rep_name}</p>
              <Link to={`/proposals/${p.proposal_id}/view`} className="inline-flex items-center gap-1 text-primary font-semibold hover:underline" data-testid="project-proposal-link"><i className="fa-solid fa-file-invoice" />Open signed proposal</Link>
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Timeline</p>
            <ol className="relative border-l border-border ml-2 space-y-3">
              {(p.stage_history || []).slice().reverse().map((h, i) => (
                <li key={i} className="ml-4"><span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full" style={{ background: STAGE_COLOR[h.stage] || BLUE }} /><p className="text-xs font-semibold text-text-primary">{stages.find(s => s.key === h.stage)?.label || h.stage}</p><p className="text-[10px] text-text-muted">{new Date(h.at).toLocaleString()} · {h.by}</p></li>
              ))}
            </ol>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Notes</p>
            <div className="space-y-2 mb-2">{(p.notes || []).slice().reverse().map((n, i) => <div key={i} className="text-xs p-2.5 rounded-lg bg-surface-alt border border-border"><p className="text-text-primary">{n.text}</p><p className="text-[10px] text-text-muted mt-1">{new Date(n.at).toLocaleString()} · {n.by}</p></div>)}</div>
            <div className="flex gap-2"><input value={note} onChange={e => setNote(e.target.value)} className="input-field h-9 text-sm flex-1" placeholder="Add a note (permit #, inspector, ETA…)" data-testid="project-note-input" /><button onClick={() => { if (note.trim()) { onPatch(p, { note }); setNote(''); } }} className="px-3 rounded-lg text-xs font-bold text-white" style={{ background: BLUE }} data-testid="project-note-add">Add</button></div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function InstallPipeline() {
  const [data, setData] = useState({ projects: [], stats: null, stages: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [wonProposals, setWonProposals] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, pr] = await Promise.all([api.get('/api/projects'), api.get('/api/proposals')]);
      setData(r.data);
      const ids = new Set(r.data.projects.map(p => p.proposal_id));
      setWonProposals((pr.data.proposals || []).filter(p => (p.portal_status === 'accepted' || p.status === 'won') && !ids.has(p.id)));
      if (open) setOpen(r.data.projects.find(p => p.id === open.id) || null);
    } catch { toast.error('Could not load install pipeline'); }
    setLoading(false);
  }, [open]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const patch = async (p, body) => {
    try {
      const r = await api.patch(`/api/projects/${p.id}`, body);
      setData(d => ({ ...d, projects: d.projects.map(x => x.id === p.id ? { ...x, ...r.data, days_in_stage: body.stage ? 0 : x.days_in_stage } : x) }));
      if (open?.id === p.id) setOpen(o => ({ ...o, ...r.data }));
      if (body.stage) { toast.success(`${p.lead_name} → ${data.stages.find(s => s.key === body.stage)?.label || body.stage}`); load(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const importWon = async (prop) => {
    try { await api.post('/api/projects', { proposal_id: prop.id }); toast.success(`Project opened for ${prop.lead_name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not create project'); }
  };

  const S = data.stats;
  const board = [...data.stages, { key: 'on_hold', label: 'On Hold' }];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="install-pipeline-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: AMBER }}>Signed → Powered on</p>
          <h1 className="text-3xl font-heading font-black text-text-primary">Install Pipeline</h1>
          <p className="text-sm text-text-muted mt-1">Every signed proposal becomes a project automatically. Drive it from site survey to utility PTO.</p>
        </div>
        {wonProposals.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="won-import-strip">
            <span className="text-xs text-text-muted">Accepted proposals not yet tracked:</span>
            {wonProposals.slice(0, 4).map(p => <button key={p.id} onClick={() => importWon(p)} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" data-testid={`import-won-${p.id}`}><i className="fa-solid fa-plus mr-1" />{p.lead_name}</button>)}
          </div>
        )}
      </div>

      {S && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Stat label="Active projects" value={S.active} sub={`${S.total} total`} testid="stat-active" />
          <Stat label="Active kW" value={`${S.active_kw} kW`} sub="under contract" accent={BLUE} testid="stat-kw" />
          <Stat label="Contract value" value={$(S.active_value)} sub="in pipeline" accent="#16A34A" testid="stat-value" />
          <Stat label="Installed" value={`${S.installed_kw} kW`} sub={`${S.complete} systems live`} accent={NAVY} testid="stat-installed" />
          <Stat label="Behind schedule" value={S.behind} sub="past target + 14d" accent={S.behind ? '#DC2626' : undefined} testid="stat-behind" />
          <Stat label="Avg cycle" value={S.avg_cycle_days != null ? `${S.avg_cycle_days}d` : '—'} sub="signed → powered on" testid="stat-cycle" />
        </div>
      )}

      {loading ? <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>
        : data.projects.length === 0 ? (
          <div className="card p-12 text-center" data-testid="pipeline-empty">
            <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center text-white text-2xl mb-4" style={{ background: NAVY }}><i className="fa-solid fa-helmet-safety" /></div>
            <h3 className="font-heading font-bold text-lg">No installs yet</h3>
            <p className="text-sm text-text-muted mt-1 max-w-md mx-auto">When a homeowner accepts and e-signs a web proposal, the project lands here automatically. You can also import any accepted proposal above.</p>
            <Link to="/proposals" className="inline-flex mt-5 text-sm font-bold px-5 py-2.5 rounded-xl text-[#0C2417]" style={{ background: AMBER }} data-testid="pipeline-goto-proposals">Go to Proposal Studio</Link>
          </div>
        ) : (
          <div className="overflow-x-auto pb-4 -mx-2 px-2">
            <div className="flex gap-3 min-w-max" data-testid="pipeline-board">
              {board.map(s => {
                const col = data.projects.filter(p => p.stage === s.key);
                return (
                  <div key={s.key} className="w-64 flex-shrink-0" data-testid={`column-${s.key}`}>
                    <div className="flex items-center justify-between mb-2 px-1">
                      <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_COLOR[s.key] }} /><p className="text-xs font-bold uppercase tracking-wider text-text-secondary">{s.label}</p></div>
                      <span className="text-[11px] font-mono font-bold text-text-muted">{col.length}</span>
                    </div>
                    <div className="rounded-2xl bg-surface-alt/60 border border-border/60 p-2 space-y-2 min-h-[140px]">
                      {col.map(p => <ProjectCard key={p.id} p={p} stages={data.stages} onMove={(pp, st) => patch(pp, { stage: st })} onOpen={setOpen} />)}
                      {col.length === 0 && <p className="text-[11px] text-text-muted text-center py-8">—</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {open && <ProjectDrawer p={open} stages={data.stages} onClose={() => setOpen(null)} onPatch={patch} />}
    </div>
  );
}
